import { ArrowDown, ArrowUp } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { SitePage } from "../../../api/types";
import { blockLabel } from "../../../model/content";

/** 屏 4 左栏上半:页面切换 chip + 当前页区块列表(点选编辑,上下箭头改序,不拖拽)。 */
export function PageBlockList({
  pages,
  page,
  activeBlockId,
  busy,
  onSelectPage,
  onSelectBlock,
  onMove,
}: {
  pages: SitePage[];
  page: SitePage | undefined;
  activeBlockId: string | null;
  busy: boolean;
  onSelectPage: (slug: string) => void;
  onSelectBlock: (blockId: string) => void;
  onMove: (blockId: string, dir: -1 | 1) => void;
}) {
  return (
    <>
      <nav aria-label="页面" className={cn("border border-border bg-card p-3", CARD_WALL_RADIUS)}>
        <h2 className="mb-2 text-xs font-semibold text-muted-foreground">页面</h2>
        <ul className="flex flex-wrap gap-1.5">
          {pages.map((p) => (
            <li key={p.slug}>
              <button
                type="button"
                aria-pressed={p.slug === page?.slug}
                onClick={() => onSelectPage(p.slug)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  p.slug === page?.slug
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-foreground hover:border-primary/50"
                )}
              >
                {p.title}{" "}
                <span className="text-2xs text-muted-foreground">{p.blocks.length} 区块</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {page && (
        <section
          aria-label="区块列表"
          className={cn("border border-border bg-card p-3", CARD_WALL_RADIUS)}
        >
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">{page.title}区块</h2>
          <ul className="space-y-1">
            {page.blocks.map((b, i) => {
              const label = blockLabel(b.type);
              const on = b.id === activeBlockId;
              return (
                <li key={b.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() => onSelectBlock(b.id)}
                    className={cn(
                      "flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors",
                      on
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-transparent text-foreground hover:bg-accent"
                    )}
                  >
                    <span className="truncate font-medium">{label}</span>{" "}
                    <code className="shrink-0 font-mono text-2xs text-muted-foreground">
                      {b.type}
                    </code>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-label={`上移 ${label}`}
                    disabled={i === 0 || busy}
                    onClick={() => onMove(b.id, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-label={`下移 ${label}`}
                    disabled={i === page.blocks.length - 1 || busy}
                    onClick={() => onMove(b.id, 1)}
                  >
                    <ArrowDown />
                  </Button>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-2xs text-muted-foreground">上下箭头改序 · 点选编辑</p>
        </section>
      )}
    </>
  );
}
